import React, { useState, useRef } from "react";
import styled from "styled-components/macro";
import { IconButton, Menu, MenuItem, Switch, ListItemIcon, ListItemText } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentPlus, faVolume, faBarsSort, faCog } from "@duocards/pro-regular-svg-icons";
import { dispatchEvent, EVENT } from "tools/events";
import { useGetState, useSetState } from "components/ReduxProvider";
import { Trans } from "react-i18next";
import { useSetLastImpulse } from "hooks/useSetLastImpulse";
import { useSetFlag } from "queries/users/setFlag";
import { DifficultyEnum } from "sharedJs__generated/constants";
import { useViewerQuery } from "queries/viewerQuery";
import StarIcon from "components/other/StarIcon";
import { SUBSCRIPTION } from "root/profile/ProfileRouter.config";
import { useNavigate } from "react-router-dom";
import { AssistantMode } from "components/assistant/__generated__/AssistantMutation.graphql";
import { ideasConfig, useChatIdeasSettings } from "hooks/useChatIdeas";
import { useListenVocabSettings, useListenVocabTranslationSupported } from "./ListenVocabControls";

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  font-size: 12px;
  width: 18px !important;
  height: 18px;
  color: ${(p) => p.theme.duo.color.text};
`;

const Disabled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.5;
  background-color: #fff;
`;

const SStarIcon = styled(StarIcon)`
  top: 2px;
  left: 32px;
`;

const SectionListItemText = styled(ListItemText)`
  color: ${(p) => p.theme.duo.palette.greyDark};
  text-transform: uppercase;
  > span {
    font-size: 14px !important;
    font-weight: 600;
  }
`;

const NoFadeMenuItem = styled(MenuItem)`
  &.Mui-disabled {
    opacity: 1;
  }
`;

type Props = {
  className?: string;
  mode?: AssistantMode;
};

const HamburgerMenu: React.FC<Props> = ({ className, mode }) => {
  const { getFlag, isSubscriber } = useViewerQuery();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const difficultyAnchorRef = useRef<HTMLLIElement>(null);
  const [open, setOpen] = useState(false);
  const [difficultyOpen, setDifficultyOpen] = useState(false);
  const setUserFlag = useSetFlag();
  const assistantAutoSpeechOff = useGetState("assistantAutoSpeechOff");
  const setAssistantAutoSpeechOff = useSetState("assistantAutoSpeechOff");
  const [chatIdeasSettings, setChatIdeasSettings] = useChatIdeasSettings({ mode });
  const [listenVocabSettings, setListenVocabSettings] = useListenVocabSettings();
  const myDifficulty = getFlag("myDifficulty")?.value;
  const autoSpeechEnabled = !assistantAutoSpeechOff;
  const navigate = useNavigate();
  const showIdeasSection = mode !== undefined ? Boolean(ideasConfig[mode]) : false;
  const setLastImpulse = useSetLastImpulse();
  const translationSupported = useListenVocabTranslationSupported();
  const showListenVocabSection = mode === "listenVocab" && translationSupported;

  const handleMenuOpen = () => {
    setOpen(true);
  };

  const handleMenuClose = () => {
    handleCloseDifficulty();
    setOpen(false);
  };

  const handleNewChat = () => {
    dispatchEvent(EVENT.resetAssistant);
    handleMenuClose();
  };

  const handleToggleAutoSpeech = () => {
    if (!isSubscriber) {
      setLastImpulse("autoSpeechFeature");
      navigate(SUBSCRIPTION.url());
      return;
    }
    setAssistantAutoSpeechOff(!assistantAutoSpeechOff);
    handleMenuClose();
  };

  const handleToggleShowIdeas = () => {
    const newSettings = { ...chatIdeasSettings, active: !chatIdeasSettings.active };
    setChatIdeasSettings(newSettings);
    dispatchEvent(EVENT.regenerateChatIdeas, newSettings);
  };

  const handleToggleTranslateIdeas = () => {
    if (!chatIdeasSettings.active) return;
    const newSettings = { ...chatIdeasSettings, withTranslation: !chatIdeasSettings.withTranslation };
    setChatIdeasSettings(newSettings);
    dispatchEvent(EVENT.regenerateChatIdeas, newSettings);
  };

  const handleToggleListenVocabTranslation = () => {
    const enabled = !listenVocabSettings.withTranslation;
    setListenVocabSettings({ ...listenVocabSettings, withTranslation: enabled });
    dispatchEvent(EVENT.resetAssistant);
  };

  const handleCloseDifficulty = () => {
    setDifficultyOpen(false);
  };

  const handleToggleDifficulty = () => {
    setDifficultyOpen(!difficultyOpen);
  };

  const handleChangeDifficulty = (difficulty: string) => {
    setUserFlag({ name: "myDifficulty", value: difficulty });
    handleMenuClose();
  };

  const difficultyToNumber: Record<string, number> = {
    [DifficultyEnum.beginner]: 1,
    [DifficultyEnum.easy]: 2,
    [DifficultyEnum.moderate]: 3,
    [DifficultyEnum.hard]: 4
  };

  return (
    <div className={className}>
      <IconButton
        ref={anchorRef}
        aria-label="hamburger menu"
        aria-controls={open ? "hamburger-menu" : undefined}
        aria-haspopup="true"
        onClick={handleMenuOpen}
        color="inherit"
      >
        <StyledFontAwesomeIcon icon={faCog} />
      </IconButton>
      <Menu
        id="hamburger-menu"
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <MenuItem onClick={handleNewChat}>
          <ListItemIcon>
            <StyledFontAwesomeIcon icon={faCommentPlus} />
          </ListItemIcon>
          <ListItemText>
            <Trans>New chat</Trans>
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleToggleAutoSpeech}>
          {!isSubscriber && <Disabled />}
          <ListItemIcon>
            <FontAwesomeIcon icon={faVolume} />
            <SStarIcon unconditional={false} />
          </ListItemIcon>
          <ListItemText>
            <Trans>Autoplay</Trans>
          </ListItemText>
          <Switch checked={autoSpeechEnabled && isSubscriber} size="small"></Switch>
        </MenuItem>

        <MenuItem component="li" ref={difficultyAnchorRef} onClick={handleToggleDifficulty}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faBarsSort}></FontAwesomeIcon>
            {myDifficulty && difficultyToNumber[myDifficulty]}
          </ListItemIcon>
          <ListItemText>
            <Trans>Difficulty</Trans>
          </ListItemText>
          <Menu
            id="difficulty-menu"
            anchorEl={difficultyAnchorRef.current}
            open={difficultyOpen}
            onClose={handleCloseDifficulty}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center"
            }}
          >
            {Object.values(DifficultyEnum).map((difficulty) => (
              <MenuItem key={difficulty} onClick={() => handleChangeDifficulty(difficulty)}>
                <ListItemText>
                  {myDifficulty === difficulty ? (
                    <b>
                      {difficultyToNumber[myDifficulty]}. <Trans>{difficulty}</Trans>
                    </b>
                  ) : (
                    <>
                      {difficultyToNumber[difficulty]}. <Trans>{difficulty}</Trans>
                    </>
                  )}
                </ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </MenuItem>

        {showIdeasSection &&
          mode !== undefined && [
            <NoFadeMenuItem disabled key="ideas-header">
              <SectionListItemText>
                <Trans>{ideasConfig[mode]?.optionsLabel}</Trans>
              </SectionListItemText>
            </NoFadeMenuItem>,
            mode === "letsPractise" ? null : (
              <MenuItem onClick={handleToggleShowIdeas} key="ideas-show">
                <ListItemText>
                  <Trans>Show {ideasConfig[mode]?.optionsLabel}</Trans>
                </ListItemText>
                <Switch checked={chatIdeasSettings.active} size="small" />
              </MenuItem>
            ),
            <MenuItem onClick={handleToggleTranslateIdeas} disabled={!chatIdeasSettings.active} key="ideas-translate">
              <ListItemText>
                <Trans>Translate {ideasConfig[mode]?.optionsLabel}</Trans>
              </ListItemText>
              <Switch checked={chatIdeasSettings.withTranslation} size="small" disabled={!chatIdeasSettings.active} />
            </MenuItem>
          ]}

        {showListenVocabSection && [
          <NoFadeMenuItem disabled key="listen-header">
            <SectionListItemText>
              <Trans>Listen options</Trans>
            </SectionListItemText>
          </NoFadeMenuItem>,
          <MenuItem onClick={handleToggleListenVocabTranslation} key="listen-translation">
            <ListItemText>
              <Trans>With translation</Trans>
            </ListItemText>
            <Switch checked={listenVocabSettings.withTranslation} size="small" />
          </MenuItem>
        ]}
      </Menu>
    </div>
  );
};

export default HamburgerMenu;
